// src/components/ZeitmessungFelder.jsx
//
// Ein Dropdown fuer die optionale Zeitmessung einer Station (Anlegen- und
// Bearbeiten-Dialog teilen es sich). Countdown und Stoppuhr schliessen sich aus:
// ein Spiel hat entweder eine feste Spielzeit (Countdown) oder es wird eine Zeit
// gemessen (Stoppuhr) – daher eine Auswahl statt zwei getrennter Felder.
//
// Gespeichert wird weiter in den bisherigen Feldern (`stopwatch_mode`,
// `countdown_seconds`) plus `stopwatch_max_seconds` und `stopwatch_attempts`;
// bestehende Stationen bleiben dadurch unveraendert gueltig.

import Input from "./ui/Input";
import Select from "./ui/Select";

/** Hoechstzahl Versuche je Teilnehmer (muss zum Backend passen). */
export const MAX_VERSUCHE = 5;

/** Auswahlwert aus den Stationsfeldern ableiten. */
export function zeitmessungVon(station) {
  const modus = station?.stopwatch_mode;
  if (modus === "total" || modus === "per_member") return modus;
  if (Number(station?.countdown_seconds) > 0) return "countdown";
  return "off";
}

/** Eingabefeld-Text einer Sekundenzahl (0/fehlend = leer). */
export const sekundenText = (wert) => (Number(wert) > 0 ? String(Math.round(Number(wert))) : "");

const alsSekunden = (text) => Math.max(0, Math.round(Number(text)) || 0);

/** Eingabefeld-Text der Versuchszahl (1/fehlend = leer, also "ein Lauf"). */
export const versucheText = (wert) => (Number(wert) > 1 ? String(Math.round(Number(wert))) : "");

const alsVersuche = (text) => Math.min(MAX_VERSUCHE, Math.max(1, Math.round(Number(text)) || 1));

/**
 * Die drei Stationsfelder fuer den Payload. Bei einer Schaetzstation gibt es
 * nichts zu messen – alles aus. Die jeweils nicht gewaehlte Messart wird
 * ausdruecklich geleert, damit nie beides gleichzeitig gespeichert ist.
 */
export function zeitmessungPayload({ art, countdownSeconds, maxSeconds, versuche, istSchaetzstation }) {
  if (istSchaetzstation) {
    return { stopwatch_mode: "off", countdown_seconds: 0, stopwatch_max_seconds: 0, stopwatch_attempts: 1 };
  }
  const stoppuhr = art === "total" || art === "per_member";
  return {
    stopwatch_mode: stoppuhr ? art : "off",
    countdown_seconds: art === "countdown" ? alsSekunden(countdownSeconds) : 0,
    stopwatch_max_seconds: stoppuhr ? alsSekunden(maxSeconds) : 0,
    // Versuche gibt es nur bei "pro Teilnehmer".
    stopwatch_attempts: art === "per_member" ? alsVersuche(versuche) : 1,
  };
}

export default function ZeitmessungFelder({
  art,
  onArt,
  countdownSeconds,
  onCountdown,
  maxSeconds,
  onMax,
  versuche = "",
  onVersuche = () => {},
  disabled = false,
  zeigeHinweise = true,
}) {
  return (
    <div className="grid gap-2">
      <Select
        label="Zeitmessung (für Zeitspiele)"
        value={art}
        onChange={(e) => onArt(e.target.value)}
        options={[
          { value: "off", label: "Keine" },
          { value: "countdown", label: "Countdown (feste Spielzeit, klingelt bei 0)" },
          { value: "total", label: "Stoppuhr – Gesamtzeit (eine Uhr fürs Team)" },
          { value: "per_member", label: "Stoppuhr – pro Teilnehmer (Summe der Zeiten)" },
        ]}
        disabled={disabled}
      />

      {art === "countdown" && (
        <div>
          <Input
            type="number"
            label="Countdown in Sekunden"
            value={countdownSeconds}
            onChange={(e) => onCountdown(e.target.value)}
            placeholder="z. B. 60"
            min="1"
            max="3600"
            step="1"
            disabled={disabled}
          />
          {zeigeHinweise && (
            <p className="mt-1 text-xs text-secondary-400">
              Der Betreuer startet den Countdown im Ergebnisdialog, am Ende klingelt es. Das
              Ergebnis trägt er weiterhin selbst ein.
            </p>
          )}
        </div>
      )}

      {(art === "total" || art === "per_member") && (
        <div>
          <Input
            type="number"
            label={
              art === "per_member"
                ? "Maximalzeit je Teilnehmer in Sekunden (optional)"
                : "Maximalzeit in Sekunden (optional)"
            }
            value={maxSeconds}
            onChange={(e) => onMax(e.target.value)}
            placeholder="leer = keine Maximalzeit"
            min="0"
            max="3600"
            step="1"
            disabled={disabled}
          />
          {zeigeHinweise && (
            <p className="mt-1 text-xs text-secondary-400">
              Die gestoppte Zeit (Sekunden) wird ins Ergebnisfeld übernommen und bleibt dort
              editierbar. Mit Maximalzeit stoppt die Uhr dort von selbst und zählt genau diese
              Zeit
              {art === "per_member" ? " für den jeweiligen Teilnehmer" : " (wird sofort übernommen)"}.
              Bei „Niedriger ist besser“ am fairsten.
            </p>
          )}
        </div>
      )}

      {art === "per_member" && (
        <div>
          <Input
            type="number"
            label="Versuche je Teilnehmer"
            value={versuche}
            onChange={(e) => onVersuche(e.target.value)}
            placeholder="leer = 1 Versuch"
            min="1"
            max={String(MAX_VERSUCHE)}
            step="1"
            disabled={disabled}
          />
          {zeigeHinweise && (
            <p className="mt-1 text-xs text-secondary-400">
              Ab 2 kann der Betreuer jeden Lauf als Versuch speichern (max. {MAX_VERSUCHE}); je
              Teilnehmer zählt der beste – nach der Wertungsrichtung der Station.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
